import * as React from 'react';
import {RouteComponentProps} from "react-router";
import {Checklist, ErrorResponse, Item, Rental, Returnal, SaveChecklist} from "../api/dtos/dtos";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay,
} from "../app/ApplicationContext";
import {ErrorAlert} from "../common/ErrorAlert";
import ChecklistForm from "./ChecklistForm";
import furoApi from "../api/FuroApi";
import {showConfirm, showSaveSuccessNotification} from "../utils/dialogs";
import ItemDetailsAlert from "../item-details/ItemDetailsAlert";
import {checklistIdsFromPathParams} from "../utils/router-utils";
import {setAppHeaderSubtitle} from "../app/AppHeader";
import * as paths from "../paths";
import {ChecklistFormMode} from "./dtos";
import ButtonBar from "../common/ButtonBar";
import {Button} from "antd";
import locales from "../locales";
import accountProvider from "../account/AccountProvider";
import {
  AlertController,
  bindAlertController,
  GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";

interface CreateChecklistPageState {
  rentalItemId: number;
  rentalId?: number;
  returnalId?: number;
  item?: Item;
  error?: ErrorResponse;
  rentalChecklist?: Checklist;
}

class CreateChecklistPage extends React.Component<RouteComponentProps, CreateChecklistPageState> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly alertController?: AlertController;

  constructor(props: RouteComponentProps) {
    super(props);
    const ids = checklistIdsFromPathParams(this.props);

    this.state = {
      error: undefined,
      rentalItemId: ids.rentalItemId,
      rentalId: ids.rentalId,
      returnalId: ids.returnalId,
      item: undefined,
      rentalChecklist: undefined,
    };

  }

  public componentDidMount() {
    this.loadItem();
  }

  public render(): React.ReactNode {
    const {rentalItemId, rentalId, returnalId, item, rentalChecklist} = this.state;
    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <GlobalAlertContextConsumer>
          {(state) => bindAlertController(state.checklist, this)}
        </GlobalAlertContextConsumer>

        {item && <ItemDetailsAlert item={item} style={{marginBottom: 24}}/>}
        {accountProvider.isAdmin() &&
            <ButtonBar>
                <Button
                    type="default"
                    onClick={this.onIgnore}
                >
                  {locales.checklist_ignore}
                </Button>
            </ButtonBar>
        }
        <ChecklistForm
          mode={rentalId ? ChecklistFormMode.CREATE_RENTAL : ChecklistFormMode.CREATE_RETURNAL}
          editable={true}
          rentalItemId={rentalItemId}
          rentalId={rentalId ?? undefined}
          returnalId={returnalId ?? undefined}
          rentalChecklist={rentalChecklist}
          onFormSubmit={this.onFormSubmit}
          onFormCancel={this.onFormCancel}
        />

        {this.state.error && <ErrorAlert style={{marginTop: '16px'}} error={this.state.error}/>}
      </>
    );
  }

  private onFormCancel = () => {
    this.props.history.goBack();
  };

  private onFormSubmit = (saveChecklist: SaveChecklist) => {
    const promise = furoApi.checklist.create(saveChecklist)
      .then(this.onSaveSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onIgnore = () => {
    showConfirm(locales.checklist_ignore_confirm, this.createIgnoredChecklist);
  };

  private createIgnoredChecklist = () => {
    const {rentalId, returnalId, rentalItemId} = this.state;
    const saveChecklist: SaveChecklist = {
      id: null,
      rentalItemId,
      rentalId,
      returnalId,
      properties: [],
      comment: null,
      ignored: true,
    };

    const promise = furoApi.checklist.create(saveChecklist)
      .then(this.onSaveSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onSaveSuccess = (id: number) => {
    showSaveSuccessNotification();
    this.alertController?.refreshAlertCount();
    this.props.history.replace(paths.viewChecklistPath(id));
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  }

  private loadItem = () => {
    if (this.state.rentalId !== undefined) {
      const promise = furoApi.rentals.get(this.state.rentalId)
        .then(this.onItemLoaded)
        .catch(this.onApiError);
      withLoadingOverlay(promise, this.loadingHandler);
    } else if (this.state.returnalId !== undefined) {
      const promise = furoApi.returns.get(this.state.returnalId)
        .then(this.onItemLoaded)
        .catch(this.onApiError);
      withLoadingOverlay(promise, this.loadingHandler);
    }
  }

  private onItemLoaded = (r: Rental | Returnal) => {
    const i = r.rentalItems?.find(item => item.id === this.state.rentalItemId);
    if (i !== undefined) {
      this.setState({
        item: i.item,
        error: undefined,
      });
      if (i.rentalChecklistId !== null) {
        const promise = furoApi.checklist.get(i.rentalChecklistId)
          .then(this.onRentalChecklistLoaded)
          .catch(this.onApiError);
        withLoadingOverlay(promise, this.loadingHandler);
      }
    }
    setAppHeaderSubtitle(this.props, r.idString ?? "");
  };

  private onRentalChecklistLoaded = (c: Checklist) => {
    this.setState({
      rentalChecklist: c,
      error: undefined,
    });
  };
}

export default CreateChecklistPage;


// WEBPACK FOOTER //
// ./src/checklist/CreateChecklistPage.tsx